import { useMemo, useState, type ReactElement } from 'react';
import {
  ListChecks,
  Loader2,
  ExternalLink,
  ChevronDown,
  ShieldCheck,
  Ban,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  SUPPORT_READONLY_TOOLTIP,
  ADD_TO_LIST_MASKED_TOOLTIP,
} from '@/components/alertActionTooltipCopy';
import { useAddToListFromAlertsEnabled } from '@/hooks/useAddToListFromAlertsEnabled';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast, TOAST_DEFAULT_DURATION_MS } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import {
  useAddPolicyExceptionFromAlert,
  type AddFromAlertScope,
} from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { ApiError } from '@kirin/api-client/errors';
import {
  PolicyExceptionAction,
  type CoveringPolicyRule,
} from '@kirin/api-client/contracts/policy-exceptions';
import type { AlertSourceDetails } from '@kirin/api-client/contracts/alerts';
import {
  evaluateSourceDetailsRule,
  sourceDetailsEntityName,
  sourceDetailsEntityListLabel,
} from '@kirin/api-client/helpers/source-details';
import {
  ALLOW_BLOCK_UI_CONFIG,
  isAllowBlockPolicyKey,
} from '@/components/policylist/allow-block/config';
import { POLICIES_ALLOW_BLOCK_SLUG } from '@/lib/policyPaths';

/**
 * Shared min-width so the trigger keeps a stable footprint in the actions
 * column. Kept in lock-step with the actions-column width math in the org/user
 * columns factories.
 */
export const ADD_TO_LIST_BUTTON_WIDTH_CLASS = 'min-w-[7.5rem] justify-center';

/** Human label for the chosen list. */
function actionLabel(action: PolicyExceptionAction): 'Allow' | 'Block' {
  return action === PolicyExceptionAction.Allow ? 'Allow' : 'Block';
}

/** Display labels for the identity fields carried in a rule object. */
const RULE_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  publisher: 'Publisher',
  version: 'Version',
  type: 'Type',
};

/**
 * Render a rule object as a readable, comma-separated "Label: value" list for
 * the stop dialogs. Falls back to any unmapped keys verbatim so we never hide a
 * matching field from the user.
 */
function formatRuleObject(ruleObject: Record<string, unknown>): string {
  const parts = Object.entries(ruleObject)
    .filter(([, value]) => value != null && String(value).trim().length > 0)
    .map(
      ([key, value]) => `${RULE_FIELD_LABELS[key] ?? key}: ${String(value)}`
    );
  return parts.length > 0 ? parts.join(', ') : '(matches everything)';
}

/**
 * Map a mutation failure to a short, user-friendly reason for the error toast.
 * The two "stop" outcomes are successful (2xx) responses handled separately —
 * only genuine failures reach here.
 */
function failureReason(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return 'could not reach the server';
    if (error.status === 401 || error.status === 403) {
      return 'you do not have permission';
    }
    if (error.status === 400) return 'the entry is not valid';
    if (error.status === 404) return 'the list is not available';
  }
  return 'something went wrong';
}

/** Result of a "stop" outcome, retained to drive the stop dialog. */
interface StopState {
  action: PolicyExceptionAction;
  isBroadRule: boolean;
  coveringRule: CoveringPolicyRule;
}

/**
 * Props for {@link AlertAddToListButton}.
 */
export interface AlertAddToListButtonProps {
  /** Organization-shared or account (personal/singleton) scope. */
  scope: AddFromAlertScope;
  /** Masked source details from the alert (KIR-3529). */
  sourceDetails: AlertSourceDetails | undefined;
  /** Alert key, threaded into analytics. */
  alertKey: string;
  /** Optional className applied to the trigger button. */
  className?: string;
  /** Optional analytics dimensions. */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
}

/**
 * "Add to Allow/Block List" control for a grouped alert (KIR-3558).
 *
 * Renders a dropdown with "Add to Allow list" / "Add to Block list". Picking a
 * list opens a confirmation dialog; on confirm the exact entity the alert
 * surfaces (built from its `sourceDetails`) is added to that Allow/Block list
 * via the shared policy-exception flow. The originating alert is unaffected.
 *
 * Outcomes:
 * - **added** → 4s success toast.
 * - **already on list** / **covered by a broad rule** → a stop dialog showing
 *   the covering rule and a "Go to Policies" link (opens the relevant list in a
 *   new tab). No change is made.
 * - **failure** → destructive toast with a friendly reason.
 *
 * Gates:
 * - **Feature flags** — hidden unless BOTH `add_to_list_from_alerts` and
 *   `alert_source_details` are on (mirrors the composite backend gate).
 * - **List capability** — hidden when the alert has no `sourceDetails` or no
 *   rule can be built (non-extension/MCP alerts); disabled (grayed, with a
 *   tooltip) when the entity is list-capable but its identity is redacted beyond
 *   what we can safely turn into a rule (`capability === 'masked'`).
 * - **Role** (org scope only) — hidden for members; disabled (grayed, with a
 *   tooltip) for support; enabled for owner/manager. Account scope is never
 *   gated — a user manages their own lists.
 *
 * When a masked field is representable as a host-anchored prefix wildcard, the
 * confirmation dialog explains the wildcard and previews the exact rule before
 * the user commits (KIR-3558).
 */
export function AlertAddToListButton({
  scope,
  sourceDetails,
  alertKey,
  className,
  analyticsContext,
}: AlertAddToListButtonProps): ReactElement | null {
  const { enabled, isLoading: flagsLoading } = useAddToListFromAlertsEnabled();

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  // The rule to add, derived once from the alert's source details, plus its
  // list-capability (available / masked / unavailable).
  const evaluation = useMemo(
    () =>
      sourceDetails
        ? evaluateSourceDetailsRule(sourceDetails)
        : ({ capability: 'unavailable', rule: null } as const),
    [sourceDetails]
  );
  const rule = evaluation.rule;

  const addMutation = useAddPolicyExceptionFromAlert(scope, rule?.policyKey);
  const { toast } = useToast();

  const [pendingAction, setPendingAction] =
    useState<PolicyExceptionAction | null>(null);
  const [stop, setStop] = useState<StopState | null>(null);

  if (flagsLoading || !enabled) return null;
  // Hide only for alerts that aren't list-capable at all.
  if (!sourceDetails || evaluation.capability === 'unavailable') return null;
  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  // List-capable but the identity is redacted beyond a safe rule: show the
  // action disabled with an explanation instead of hiding it.
  const maskedUnavailable = evaluation.capability === 'masked';
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const entityName = sourceDetailsEntityName(sourceDetails);
  const entityListLabel = sourceDetailsEntityListLabel(sourceDetails);
  const precedenceNote = (action: PolicyExceptionAction): string => {
    if (!rule || !isAllowBlockPolicyKey(rule.policyKey)) return '';
    const config = ALLOW_BLOCK_UI_CONFIG[rule.policyKey];
    return action === PolicyExceptionAction.Allow
      ? config.allowedNote
      : config.blockedNote;
  };

  const policiesBasePath =
    scope.kind === 'organization'
      ? `/${scope.organizationId}/policies`
      : '/dashboard/policies';
  const openPolicies = (): void => {
    if (!rule) return;
    // KIR-3982: allow/block policies live on the Allow / Block Lists sub-page,
    // so the deep link must target it or `?policy=` finds no card to expand.
    const subPage = isAllowBlockPolicyKey(rule.policyKey)
      ? `/${POLICIES_ALLOW_BLOCK_SLUG}`
      : '';
    const url = `${policiesBasePath}${subPage}?policy=${encodeURIComponent(rule.policyKey)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const confirmAdd = async (): Promise<void> => {
    if (pendingAction === null || !rule) return;
    const action = pendingAction;
    try {
      const result = await addMutation.mutateAsync({
        action,
        ruleObject: rule.ruleObject,
      });
      setPendingAction(null);
      if (result.status === 'added') {
        // Success feedback is a toast only. The ticket's "Undo" affordance was
        // intentionally dropped (product decision): removal is a one-click
        // action on the Policies screen, so an Undo here would duplicate that
        // surface and complicate the add-only flow. See the plan's deviations.
        toast({
          variant: 'success',
          title: `Added to ${entityListLabel} ${actionLabel(action)} list.`,
          duration: TOAST_DEFAULT_DURATION_MS,
        });
        trackEvent('alert_add_to_list', {
          scope: scope.kind,
          alert_key: alertKey,
          list: action,
          policy_key: rule.policyKey,
          result: 'added',
          severity: analyticsContext?.severity,
          type: analyticsContext?.type,
        });
        return;
      }
      // "Stop" outcome — no change was made; surface the covering rule.
      setStop({
        action,
        isBroadRule: result.status === 'covered_by_broad_rule',
        coveringRule: result.coveringRule,
      });
      trackEvent('alert_add_to_list', {
        scope: scope.kind,
        alert_key: alertKey,
        list: action,
        policy_key: rule.policyKey,
        result: result.status,
        severity: analyticsContext?.severity,
        type: analyticsContext?.type,
      });
    } catch (error) {
      setPendingAction(null);
      toast({
        variant: 'destructive',
        title: `Failed to update list – ${failureReason(error)}.`,
      });
    }
  };

  const isPending = addMutation.isPending;

  // Non-interactive states (support read-only, or a masked identity we can't
  // turn into a safe rule): render a visually-disabled trigger wrapped in a
  // focusable <span> so the explanatory Tooltip still receives hover/focus. A
  // native `disabled` button sets `pointer-events-none` and swallows those
  // events, hiding the tooltip. `pointer-events-none` on the inner Button plus
  // the no-op `onClick` keep the action unreachable; `opacity-50` is the visual
  // affordance. Dropdown/dialogs are intentionally not mounted here. Mirrors the
  // AlertExcludeButton non-excludable branch.
  if (writeDisabled || maskedUnavailable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn('inline-flex', className)}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                ADD_TO_LIST_BUTTON_WIDTH_CLASS,
                'pointer-events-none opacity-50'
              )}
              aria-disabled="true"
              aria-label="Add to Allow or Block list (unavailable)"
              onClick={event => event.preventDefault()}
            >
              <ListChecks className="mr-1 size-4" />
              <span>Add to Allow/Block List</span>
              <ChevronDown className="ml-1 size-4" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {writeDisabled
            ? SUPPORT_READONLY_TOOLTIP
            : ADD_TO_LIST_MASKED_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(ADD_TO_LIST_BUTTON_WIDTH_CLASS, className)}
                disabled={isPending}
                aria-label="Add to Allow or Block list"
              >
                {isPending ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <ListChecks className="mr-1 size-4" />
                )}
                <span>Add to Allow/Block List</span>
                <ChevronDown className="ml-1 size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {`Add ${entityName} to an Allow or Block list`}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => setPendingAction(PolicyExceptionAction.Allow)}
          >
            <ShieldCheck className="mr-2 size-4 text-green-600" />
            Add to Allow list
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setPendingAction(PolicyExceptionAction.Block)}
          >
            <Ban className="mr-2 size-4 text-red-600" />
            Add to Block list
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation dialog */}
      <Dialog
        open={pendingAction !== null}
        onOpenChange={open => {
          if (!open) setPendingAction(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingAction !== null
                ? `Add ${entityName} to the ${actionLabel(pendingAction)} list?`
                : ''}
            </DialogTitle>
            <DialogDescription>
              {pendingAction !== null ? precedenceNote(pendingAction) : ''} The
              change applies to new scans.
            </DialogDescription>
          </DialogHeader>
          {rule?.usesWildcard && (
            <>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="mb-1 font-medium">
                  Part of this address is hidden
                </div>
                <div className="text-muted-foreground">
                  This entity’s address contains a secret, so it can’t be
                  matched exactly. We’ll add a rule that matches the visible
                  prefix followed by a wildcard (<code>*</code>). The rule we’ll
                  create:
                </div>
                <div className="mt-2 font-mono text-xs text-foreground">
                  {formatRuleObject(rule.ruleObject)}
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  Please note:
                </span>{' '}
                {sourceDetails.kind === 'mcp' ? (
                  <>
                    This server puts a secret in its URL.{' '}
                    {pendingAction === PolicyExceptionAction.Allow &&
                      'Allow-listing does not remove this risk. '}
                    We recommend moving the secret into an environment variable
                    or a header, then re-scanning with Kirin.
                  </>
                ) : (
                  <>
                    This entity’s address contains a secret.{' '}
                    {pendingAction === PolicyExceptionAction.Allow &&
                      'Allow-listing does not remove this risk. '}
                    We recommend removing the secret from its configuration,
                    then re-scanning with Kirin.
                  </>
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingAction(null)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmAdd()}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-1 size-4 animate-spin" />}
              {pendingAction !== null
                ? `Add to ${actionLabel(pendingAction)} list`
                : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stop dialog (already on list / covered by a broad rule) */}
      <Dialog
        open={stop !== null}
        onOpenChange={open => {
          if (!open) setStop(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {stop?.isBroadRule
                ? 'Covered by a broad policy'
                : `Already on the ${stop ? actionLabel(stop.action) : ''} list`}
            </DialogTitle>
            <DialogDescription>
              {stop?.isBroadRule
                ? 'A broader rule already covers this entity, so nothing was added. Review it in the relevant Allow/Block list on the Policies screen — there may be other matching rules.'
                : 'This exact entity is already on the list, so nothing was added. There may be other matching rules — review them on the Policies screen.'}
            </DialogDescription>
          </DialogHeader>
          {stop && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <div className="mb-1 font-medium">
                {stop.isBroadRule ? 'Covering rule' : 'Matching entry'}
              </div>
              <div className="text-muted-foreground">
                {actionLabel(stop.coveringRule.action)} —{' '}
                {formatRuleObject(stop.coveringRule.ruleObject)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setStop(null)}>
              Close
            </Button>
            <Button type="button" onClick={openPolicies}>
              <ExternalLink className="mr-1 size-4" />
              Go to Policies
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

AlertAddToListButton.displayName = 'AlertAddToListButton';
